import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function WebhooksContactsPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Webhooks" },
  ];

  return (
    <DocLayout
      title="Webhooks - Uptime Monitor Documentation"
      description="Integrate Uptime Monitor with any system using webhooks. Send real-time notifications to your custom endpoints."
      canonical="https://uptime-monitor.io/docs/notifications/webhooks"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Webhooks"
        description="Send monitor events to your custom HTTP endpoints. Build powerful integrations and automate incident response workflows."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            What are Webhooks?
          </h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Webhooks allow Uptime Monitor to send HTTP POST requests to your endpoints when monitor
            events occur. This enables real-time integration with:
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Your custom applications and internal systems
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Third-party incident management platforms
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Custom dashboards and reporting tools
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Automation and workflow platforms
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Any service that accepts HTTP POST requests
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Setting Up Webhooks */}
      <DocSection title="Setting Up Webhooks">
        <DocCard>
          <ol className="space-y-4">
            <li>
              <div className="flex items-start">
                <DocStepIndicator number={1} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                    Create Webhook Endpoint
                  </h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Set up an HTTP endpoint on your server that can receive POST requests
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={2} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                    Add Webhook Contact
                  </h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Go to Settings → Contacts and select &quot;Webhook&quot; as the contact type
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={3} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                    Configure Webhook
                  </h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Enter the webhook URL and a name for the contact
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={4} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>Save Contact</h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Click save to add the webhook contact
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={5} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                    Assign to Monitors
                  </h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Add the webhook contact to monitors that should trigger notifications
                  </p>
                </div>
              </div>
            </li>
          </ol>
        </DocCard>
      </DocSection>

      {/* Webhook Payload */}
      <DocSection title="Webhook Payload Format">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            All monitor webhooks are sent as POST requests with a JSON payload. The format varies by
            monitor type:
          </p>

          <div className="mb-6">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              HTTPS Monitor Example
            </h3>
            <DocCodeBlock className="overflow-x-auto">
              <pre className={`text-sm ${docsColors.text.body}`}>
                <code>{`{
  "monitor_id": "507f1f77bcf86cd799439011",
  "monitor_name": "Production API",
  "monitor_settings": {
    "https": {
      "url": "https://api.example.com/health",
      "check_certificate_expiration": true,
      "http_method": "GET",
      "follow_redirect": true,
      "http_statuses": "200,201-204"
    }
  },
  "timestamp": 1705330335,
  "status": "down",
  "error": "Connection timeout after 10000ms"
}`}</code>
              </pre>
            </DocCodeBlock>
          </div>

          <div className="mb-6">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>TCP Monitor Example</h3>
            <DocCodeBlock className="overflow-x-auto">
              <pre className={`text-sm ${docsColors.text.body}`}>
                <code>{`{
  "monitor_id": "507f1f77bcf86cd799439012",
  "monitor_name": "Database Connection",
  "monitor_settings": {
    "tcp": {
      "url": "tcp://database.example.com:5432"
    }
  },
  "timestamp": 1705330400,
  "status": "up",
  "error": "Connection refused"
}`}</code>
              </pre>
            </DocCodeBlock>
          </div>

          <div className="mb-6">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Ping Monitor Example
            </h3>
            <DocCodeBlock className="overflow-x-auto">
              <pre className={`text-sm ${docsColors.text.body}`}>
                <code>{`{
  "monitor_id": "507f1f77bcf86cd799439013",
  "monitor_name": "Google DNS",
  "monitor_settings": {
    "ping": {
      "url": "ping://8.8.8.8"
    }
  },
  "timestamp": 1705330500,
  "status": "down",
  "error": "Destination host unreachable"
}`}</code>
              </pre>
            </DocCodeBlock>
          </div>

          <div className="mt-6">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Common Payload Fields
            </h3>
            <div className="space-y-2">
              <div className="flex items-start">
                <code className="text-blue-600 dark:text-blue-400 font-mono text-sm mr-3">
                  monitor_id
                </code>
                <span className={`${docsColors.text.body} text-sm`}>
                  MongoDB ObjectId of the monitor (24-character hex string)
                </span>
              </div>
              <div className="flex items-start">
                <code className="text-blue-600 dark:text-blue-400 font-mono text-sm mr-3">
                  monitor_name
                </code>
                <span className={`${docsColors.text.body} text-sm`}>
                  User-defined name of the monitor
                </span>
              </div>
              <div className="flex items-start">
                <code className="text-blue-600 dark:text-blue-400 font-mono text-sm mr-3">
                  monitor_settings
                </code>
                <span className={`${docsColors.text.body} text-sm`}>
                  Monitor configuration object (structure varies by type)
                </span>
              </div>
              <div className="flex items-start">
                <code className="text-blue-600 dark:text-blue-400 font-mono text-sm mr-3">
                  timestamp
                </code>
                <span className={`${docsColors.text.body} text-sm`}>
                  Unix timestamp (seconds) of the event
                </span>
              </div>
              <div className="flex items-start">
                <code className="text-blue-600 dark:text-blue-400 font-mono text-sm mr-3">
                  status
                </code>
                <span className={`${docsColors.text.body} text-sm`}>
                  &quot;up&quot; or &quot;down&quot; - current monitor status
                </span>
              </div>
              <div className="flex items-start">
                <code className="text-blue-600 dark:text-blue-400 font-mono text-sm mr-3">
                  error
                </code>
                <span className={`${docsColors.text.body} text-sm`}>
                  Error message (present for both down and recovery events)
                </span>
              </div>
            </div>
          </div>

          <DocAlert variant="info" className="mt-6 p-4">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Note:</strong> The monitor_settings
              structure depends on the monitor type. HTTPS monitors include fields like http_method,
              headers, and expected responses. TCP and Ping monitors only include the URL.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Webhook Security */}
      <DocSection title="Webhook Security">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Since webhooks don&apos;t support custom headers, secure your endpoint by:
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Using a unique, hard-to-guess webhook URL with a token parameter
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Implementing IP allowlisting for Uptime Monitor&apos;s servers
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Validating the payload structure and required fields
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Using HTTPS to encrypt data in transit
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Adding rate limiting to prevent abuse
            </li>
          </ul>

          <DocCodeBlock className="mt-4 p-3">
            <p className={`text-sm font-mono ${docsColors.text.body}`}>
              <span className={docsColors.text.muted}>Example secure URL:</span>
              <br />
              https://api.example.com/webhooks/uptime?token=secret-token-here
            </p>
          </DocCodeBlock>
        </DocCard>
      </DocSection>

      {/* Certificate Expiration Webhooks */}
      <DocSection title="Certificate Expiration Webhooks">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            SSL certificate expiration alerts use a different payload format:
          </p>

          <DocCodeBlock className="overflow-x-auto">
            <pre className={`text-sm ${docsColors.text.body}`}>
              <code>{`{
  "host": "example.com",
  "port": 443,
  "period": "7_days",
  "message": "example.com certificate will expire in 7 days!"
}`}</code>
            </pre>
          </DocCodeBlock>

          <div className="mt-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Period Values</h3>
            <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
              <li>
                • <code className="text-blue-600 dark:text-blue-400">EXPIRED</code> - Certificate
                has already expired
              </li>
              <li>
                • <code className="text-blue-600 dark:text-blue-400">a day</code> - Expires tomorrow
              </li>
              <li>
                • <code className="text-blue-600 dark:text-blue-400">a week</code> - Expires in 7
                days
              </li>
              <li>
                • <code className="text-blue-600 dark:text-blue-400">2 weeks</code> - Expires in 14
                days
              </li>
              <li>
                • <code className="text-blue-600 dark:text-blue-400">30 days</code> - Expires in 30
                days
              </li>
            </ul>
          </div>
        </DocCard>
      </DocSection>

      {/* Delivery Policy */}
      <DocSection title="Delivery Policy">
        <DocCard>
          <div className="space-y-4">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Webhook Delivery</h3>
              <ul className={`space-y-2 ${docsColors.text.body}`}>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  Webhooks are sent immediately when events occur
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  30-second timeout for webhook responses
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  Success is any 2xx HTTP status code
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  Single delivery attempt per event
                </li>
              </ul>
            </div>

            <DocAlert variant="warning" className="mt-4 p-3">
              <p className={`text-sm ${docsColors.text.body}`}>
                <strong className={docsColors.text.heading}>Important:</strong> Webhooks are
                delivered once without automatic retries. Ensure your endpoint is reliable and
                implements its own error handling if needed.
              </p>
            </DocAlert>
          </div>
        </DocCard>
      </DocSection>

      {/* Common Use Cases */}
      <DocSection title="Common Webhook Use Cases">
        <div className="grid gap-4">
          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
              Custom Notification Systems
            </h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Build webhook receivers that process events and route notifications to your preferred
              communication channels or internal messaging systems.
            </p>
          </DocCard>

          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Workflow Automation</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Trigger automated workflows when monitors go down - restart services, scale
              infrastructure, or create tickets in your issue tracking system.
            </p>
          </DocCard>

          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
              Data Collection & Analytics
            </h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Collect uptime data in your own databases for custom reporting, SLA calculations, or
              integration with business intelligence tools.
            </p>
          </DocCard>

          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Status Page Updates</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Automatically update your custom status pages or third-party status page services when
              monitors change state.
            </p>
          </DocCard>
        </div>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Webhook Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use secure webhook URLs</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Include a secret token in your webhook URL to authenticate requests
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Respond quickly</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Process webhooks asynchronously and return 200 OK immediately
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Implement idempotency</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use the monitor_id and timestamp to handle potential duplicate events
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use HTTPS endpoints</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Always use SSL/TLS encrypted endpoints for security
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Log webhook data</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Keep logs of received webhooks for debugging and auditing
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Example Webhook Receiver */}
      <DocSection title="Example Webhook Receiver">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>Simple Node.js Express webhook receiver:</p>

          <DocCodeBlock className="overflow-x-auto">
            <pre className={`text-sm ${docsColors.text.body}`}>
              <code>{`const express = require('express');
const app = express();

app.use(express.json());

app.post('/webhook', (req, res) => {
  // Verify webhook token from URL
  if (req.query.token !== 'your-secret-token') {
    return res.status(401).send('Unauthorized');
  }

  // Process webhook payload
  const { 
    monitor_id,
    monitor_name,
    monitor_settings,
    timestamp,
    status,
    error
  } = req.body;
  
  console.log(\`Monitor: \${monitor_name} (\${monitor_id})\`);
  console.log(\`Status: \${status}\`);
  console.log(\`Time: \${new Date(timestamp * 1000).toISOString()}\`);
  
  if (status === 'down' && error) {
    console.log(\`Error: \${error}\`);
    // Trigger your alert logic here
  } else if (status === 'up') {
    console.log(\`Monitor recovered (last error: \${error})\`);
    // Handle recovery logic here
  }
  
  // Respond immediately with success
  res.status(200).send('OK');
});

app.listen(3000, () => {
  console.log('Webhook receiver listening on port 3000');
});`}</code>
            </pre>
          </DocCodeBlock>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting Webhooks">
        <DocAlert variant="warning" className="p-6">
          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>Common Issues</h3>
          <ol className={`space-y-3 ${docsColors.text.body}`}>
            <li>
              <strong className={docsColors.text.heading}>Webhooks not received:</strong>
              <ul className="mt-1 ml-4 text-sm space-y-1">
                <li>• Check webhook URL is correct and publicly accessible</li>
                <li>• Verify no firewall blocking our IPs</li>
                <li>• Ensure webhook is assigned to monitors</li>
              </ul>
            </li>
            <li>
              <strong className={docsColors.text.heading}>Authentication failures:</strong>
              <ul className="mt-1 ml-4 text-sm space-y-1">
                <li>• Verify the webhook URL includes a secret token</li>
                <li>• Check token validation logic on your endpoint</li>
                <li>• Ensure the token in URL matches your validation</li>
              </ul>
            </li>
            <li>
              <strong className={docsColors.text.heading}>Timeouts:</strong>
              <ul className="mt-1 ml-4 text-sm space-y-1">
                <li>• Process webhooks asynchronously</li>
                <li>• Return 200 OK immediately</li>
                <li>• Handle heavy processing in background</li>
              </ul>
            </li>
          </ol>
        </DocAlert>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/email", text: "Email Notifications" }}
        next={{ href: "/docs/notifications/slack", text: "Slack" }}
      />
    </DocLayout>
  );
}
